import _ from 'lodash'
import classNames from 'classnames'
import html2canvas from 'html2canvas'
import moment from 'moment'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import apis from 'browser/app/models/apis'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { FramesManager } from 'shared-libs/components/view/frames-manager'

import { UI2PlatformContext } from '../../forms/ui2-form/ui2-platform-context'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2EmptyStateLauncher } from '../field-ui/empty-state-launcher/empty-state-launcher'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'
import { UI2SignaturePad } from '../field-ui/signature-pad/ui2-signature-pad'

import { ESignature } from 'shared-libs/generated/server-types/entity/definitions/eSignature'
import {
  UI2SignatureFieldProps,
  UI2_SIGNATURE_FIELD_DEFAULT_PROPS,
  buildSignatureEnvelope,
  isSignatureEmpty,
  isSignatureSigned,
  readSignatureImageUri,
  validateSignatureEnvelope,
} from 'shared-libs/components/ui2/fields/ui2-signature-field/ui2-signature-field-props'

import './_ui2-signature-field.scss'

const DEFAULT_EMPTY_MESSAGE_DW = 'Click to sign'
const DEFAULT_EMPTY_MESSAGE_MW = 'Tap to sign'
const DEFAULT_DISCLOSURE =
  'By checking this box, I agree to use electronic records and signatures and that the below ' +
  'signature preview will be the electronic representation of my signature whenever I use it. ' +
  'Please refer to the Electronic Record and Signature Disclosure section in Vector’s Terms of ' +
  'Service found under Settings.'

interface IUI2SignatureFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  frames?: FramesManager
  translationTable?: any
}

export interface IUI2SignatureFieldProps
  extends UI2SignatureFieldProps,
    IUI2SignatureFieldWebExtras {}

// Module-level set of `entityId::valuePath` keys signed within this browser
// session, so the immutable lock only latches for values loaded pre-signed —
// not for a signature the user just captured and whose section re-mounted.
const userSignedInSession = new Set<string>()

function getSessionKey(entity: any, frames?: FramesManager): string | null {
  const entityId = entity?.uniqueId
  const valuePath = frames?.getContext?.('valuePath')
  if (!entityId || !valuePath) return null
  const pathStr = _.isArray(valuePath) ? valuePath.join('.') : String(valuePath)
  return `${entityId}::${pathStr}`
}

/**
 * Web signature capture, shared by DW and MW.
 *
 * DW offers two capture flows: the consent-checkbox flow (default) where the
 * user ticks a box and their name is rendered in cursive then screenshotted
 * into a PNG on pre-save, and the drawing pad. MW always uses the pad — a
 * touch device has a real signing surface.
 */
export class UI2SignatureField extends React.Component<IUI2SignatureFieldProps> {
  public static contextType = UI2PlatformContext
  declare context: React.ContextType<typeof UI2PlatformContext>

  public static defaultProps: Partial<IUI2SignatureFieldProps> = {
    ...UI2_SIGNATURE_FIELD_DEFAULT_PROPS,
    signatureDisclosure: DEFAULT_DISCLOSURE,
  }

  private generatedSignatureId = uuidv4()
  private padOverlayNode: Element | undefined
  private signedAtMount: boolean

  constructor(props: IUI2SignatureFieldProps) {
    super(props)
    const sessionKey = getSessionKey(props.entity, props.frames)
    const signedInSession = sessionKey ? userSignedInSession.has(sessionKey) : false
    this.signedAtMount = isSignatureSigned(props.value) && !signedInSession
  }

  public componentWillUnmount(): void {
    this.closePad()
  }

  public render(): React.ReactNode {
    validateSignatureEnvelope(this.props.value, this.getValuePath())
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps<ESignature>)}
        isReadOnly={this.isEffectivelyReadOnly()}
        isValueEmpty={isSignatureEmpty}
        renderReadOnly={this.renderReadOnly}
      >
        {this.renderEditableBody()}
      </UI2FieldShell>
    )
  }

  // A value loaded already-signed with `immutableSignature` is locked outright.
  // That overrides prefilled mode, which would otherwise offer an Edit
  // affordance the user cannot act on.
  private isEffectivelyReadOnly(): boolean {
    return !!this.props.isReadOnly || (this.signedAtMount && !!this.props.immutableSignature)
  }

  private isMobileWeb(): boolean {
    return this.context === 'mobileWeb'
  }

  // The consent-checkbox flow depends on screenshotting a cursive name, which
  // is a pointer-device fallback. MW has a real signing surface, so it always
  // draws regardless of what the schema asked for.
  private useCheckboxFlow(): boolean {
    return !this.isMobileWeb() && !!this.props.showSignatureCheckbox
  }

  private getValuePath(): string | undefined {
    const valuePath: any = this.props.frames?.getContext?.('valuePath')
    return _.isArray(valuePath) && !_.isEmpty(valuePath) ? valuePath.join('.') : undefined
  }

  private getSignerName(): string {
    const { entity } = this.props
    const settings = entity?.api?.settings ?? apis.getSettings()
    return _.get(settings.getUser(), 'displayName', '')
  }

  private getCursiveSignature(): string {
    return _.replace(this.getSignerName(), ' ', '')
  }

  // ─── Read-only / prefilled display ──────────────────────────────────────────

  private renderReadOnly = (value: ESignature | undefined, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    const { previewLabel, nameLabel, timestampLabel } = this.props
    const signer = _.get(value, 'signer')
    const signedDate = _.get(value, 'signedDate')
    const uri = readSignatureImageUri(value)
    const hasPreview = !!uri || isSignatureSigned(value)

    if (!hasPreview && !signer && !signedDate) {
      return (
        <>
          <div className='ui2-fieldShell-readOnly-content'><UI2NoValueAvailable /></div>
          {editNode}
        </>
      )
    }

    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <div className='ui2-signatureField-readOnly'>
            {hasPreview && this.renderReadOnlyRow(previewLabel, this.renderPreview(uri))}
            {!!signer && this.renderReadOnlyRow(nameLabel, <span>{signer}</span>)}
            {!!signedDate &&
              this.renderReadOnlyRow(
                timestampLabel,
                <span>{moment(signedDate).format('MMM D, YYYY h:mm A')}</span>,
              )}
          </div>
        </div>
        {editNode}
      </>
    )
  }

  private renderReadOnlyRow(label: string, content: React.ReactNode): React.ReactNode {
    return (
      <div className='ui2-signatureField-readOnlyRow' key={label}>
        <span className='ui2-signatureField-readOnlyLabel'>{label}</span>
        <div className='ui2-signatureField-readOnlyValue'>{content}</div>
      </div>
    )
  }

  // A signed value with no image yet means the pre-save screenshot hasn't run
  // (or the server is still converting) — fall back to the live cursive render
  // so the user always sees what they signed.
  private renderPreview(uri: string | undefined): React.ReactNode {
    if (uri) {
      return <img className='ui2-signatureField-previewImage' src={uri} alt='Signature' />
    }
    return this.renderCursiveSignature(false)
  }

  // ─── Editable body ──────────────────────────────────────────────────────────

  private renderEditableBody(): React.ReactNode {
    return this.useCheckboxFlow() ? this.renderCheckboxFlow() : this.renderPadFlow()
  }

  private renderCheckboxFlow(): React.ReactNode {
    const { value, isDisabled, signatureDisclosure } = this.props
    const signed = isSignatureSigned(value)
    return (
      <div className='ui2-signatureField-consent'>
        <button
          type='button'
          role='checkbox'
          aria-checked={signed}
          className={classNames('ui2-signatureField-consentRow', {
            'ui2-signatureField-consentRow--disabled': isDisabled,
          })}
          disabled={!!isDisabled}
          onClick={() => this.handleConsentToggle(!signed)}
        >
          <span
            className={classNames('ui2-signatureField-checkbox', {
              'ui2-signatureField-checkbox--checked': signed,
              'ui2-signatureField-checkbox--disabled': isDisabled,
            })}
          >
            {signed && <Icon icon={IconNames.SMALL_TICK} iconSize={14} />}
          </span>
          <span className='ui2-signatureField-disclosure'>{signatureDisclosure}</span>
        </button>
        {signed && this.renderCursiveSignature(true)}
      </div>
    )
  }

  // The element html2canvas screenshots on pre-save. Its id must be stable
  // across renders and unique per bound path, since several signature fields
  // can share a form.
  private renderCursiveSignature(withCapture: boolean): React.ReactNode {
    const name = this.getCursiveSignature()
    const signerId = this.getSignerId()
    return (
      <div
        id={withCapture ? this.getCaptureElementId() : undefined}
        className='ui2-signatureField-generated'
      >
        <div className='ui2-signatureField-generatedCaption'>Vector eSigned by:</div>
        <div className='ui2-signatureField-generatedName'>{name}</div>
        <div className='ui2-signatureField-generatedId'>{signerId}</div>
      </div>
    )
  }

  private renderPadFlow(): React.ReactNode {
    const { value, isDisabled, placeholder } = this.props
    const uri = readSignatureImageUri(value)
    const hasValue = !isSignatureEmpty(value)

    if (!hasValue) {
      const message =
        placeholder || (this.isMobileWeb() ? DEFAULT_EMPTY_MESSAGE_MW : DEFAULT_EMPTY_MESSAGE_DW)
      return (
        <UI2EmptyStateLauncher
          onPress={this.handleOpenPad}
          message={message}
          icon={<Icon icon={IconNames.EDIT} iconSize={16} />}
          isDisabled={!!isDisabled}
        />
      )
    }

    const inner = (
      <>
        {uri ? (
          <img className='ui2-signatureField-boxImage' src={uri} alt='Signature' />
        ) : (
          this.renderCursiveSignature(false)
        )}
        {!isDisabled && <Icon icon={IconNames.CHEVRON_RIGHT} iconSize={16} />}
      </>
    )

    if (isDisabled) {
      return (
        <div className='ui2-signatureField-box ui2-signatureField-box--disabled'>{inner}</div>
      )
    }
    return (
      <button type='button' className='ui2-signatureField-box' onClick={this.handleOpenPad}>
        {inner}
      </button>
    )
  }

  // ─── Consent-checkbox capture ───────────────────────────────────────────────

  private handleConsentToggle = (nextSigned: boolean) => {
    const { onChange, entity } = this.props
    if (!nextSigned) {
      onChange?.({})
      return
    }
    this.latchSignedInSession()
    const cursive = this.getCursiveSignature()
    onChange?.(
      buildSignatureEnvelope({
        signatureType: 'checkbox',
        signature: { name: `${cursive}.png`, type: 'image', uniqueId: this.generatedSignatureId },
        signerName: this.getSignerName(),
        signerUserId: this.getSignerId(),
        signerFirmId: this.getSignerFirmId(),
        signedDate: moment().toISOString(),
      }),
    )
    entity?.addPreSaveAction?.({
      name: `screenShotGeneratedSignature-${this.getValuePath()}`,
      action: this.attachGeneratedSignature,
    })
  }

  // Pre-save hook: rasterize the cursive preview and hand the PNG to the
  // entity's multipart payload so the signature persists as a real image.
  private attachGeneratedSignature = async (entity: any): Promise<void> => {
    if (!isSignatureSigned(this.props.value)) return
    const element = document.getElementById(this.getCaptureElementId())
    if (_.isNil(element)) return
    const canvas = await html2canvas(element)
    const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve))
    if (_.isNil(blob)) return
    const cursive = this.getCursiveSignature()
    const file = new File([blob], `${cursive}.png`, { type: 'image/png' })
    const signaturePath = `${this.getValuePath()}.signature`
    entity.clearMultipartFiles(signaturePath)
    entity.addMultipartFiles(signaturePath, [{ file, uniqueId: this.generatedSignatureId }])
  }

  private getCaptureElementId(): string {
    return `ui2-signatureField-capture-${this.getValuePath() ?? this.generatedSignatureId}`
  }

  private getSignerId(): string {
    const { entity } = this.props
    const settings = entity?.api?.settings ?? apis.getSettings()
    return _.get(settings.getUser(), 'uniqueId', '')
  }

  private getSignerFirmId(): string {
    const { entity } = this.props
    const settings = entity?.api?.settings ?? apis.getSettings()
    return _.get(settings.getFirm(), 'uniqueId', '')
  }

  // ─── Pad capture ────────────────────────────────────────────────────────────

  private handleOpenPad = () => {
    const { label, previewLabel } = this.props
    this.padOverlayNode = OverlayManager.openOverlayElement(
      <Modal
        backdropClosesModal={false}
        modalDialogClassName='ui2-signatureField-padDialog'
        // The overlay mounts outside this field's DOM subtree, so the ambient
        // `ui2--mobileWeb` class can't reach it — carry the platform on the
        // paper class instead.
        modalPaperClassName={classNames('ui2-signatureField-padPaper', {
          'ui2-signatureField-padPaper--mobileWeb': this.isMobileWeb(),
        })}
      >
        <UI2SignaturePad
          title={label || previewLabel}
          portraitOnly={!this.isMobileWeb()}
          onDone={this.handlePadDone}
          onClose={this.closePad}
        />
      </Modal>,
    ) as Element
  }

  private closePad = () => {
    if (!this.padOverlayNode) return
    OverlayManager.closeOverlay(this.padOverlayNode as HTMLElement)
    this.padOverlayNode = undefined
  }

  private handlePadDone = (blob: Blob) => {
    const { entity, onChange } = this.props
    const cursive = this.getCursiveSignature()
    const uniqueId = uuidv4()
    const file = new File([blob], `${cursive}.png`, { type: 'image/png' })
    const valuePath = this.getValuePath()
    if (entity && valuePath) {
      const signaturePath = `${valuePath}.signature`
      entity.clearMultipartFiles(signaturePath)
      entity.addMultipartFiles(signaturePath, [{ file, uniqueId }])
    }
    this.latchSignedInSession()
    onChange?.(
      buildSignatureEnvelope({
        signatureType: 'pad',
        signature: {
          name: `${cursive}.png`,
          type: 'image',
          uniqueId,
          // Local object URL so the capture renders immediately, before the
          // server has converted and returned a hosted `source`.
          uri: URL.createObjectURL(file),
        },
        signerName: this.getSignerName(),
        signerUserId: this.getSignerId(),
        signerFirmId: this.getSignerFirmId(),
        signedDate: moment().toISOString(),
      }),
    )
  }

  private latchSignedInSession(): void {
    const sessionKey = getSessionKey(this.props.entity, this.props.frames)
    if (sessionKey) userSignedInSession.add(sessionKey)
  }
}
